/**
 * Reusable banner at the top of every admin page explaining what it does
 * and EXACTLY where the data appears on the public site.
 */
import React from 'react';
import { Info } from 'lucide-react';

/**
 * @param {string} title - "What this is" headline (one-line)
 * @param {string} description - 1-2 sentence plain-English description
 * @param {Array<{label:string, where:string}>} appearsAt - List of "X appears at Y" mappings
 * @param {string} [accent] - tailwind colour class (default forest)
 */
export default function HelpCallout({ title, description, appearsAt = [], accent = 'bg-forest' }) {
  return (
    <div
      className={`${accent} text-base rounded-2xl p-6 mb-8 relative overflow-hidden`}
      data-testid="admin-help-callout"
    >
      <div className="absolute inset-0 grain opacity-30 pointer-events-none"/>
      <div className="relative flex gap-4">
        <Info size={20} className="mt-1 shrink-0 text-mustard"/>
        <div className="flex-1">
          <div className="label text-mustard mb-1">What this is</div>
          <h3 className="font-display text-xl leading-tight mb-2">{title}</h3>
          <p className="text-base/85 text-sm leading-relaxed mb-4">{description}</p>
          {appearsAt.length > 0 && (
            <div>
              <div className="label text-mustard mb-2">Where it appears on the public site</div>
              <ul className="space-y-1.5">
                {appearsAt.map((a, i) => (
                  <li key={i} className="text-sm flex items-baseline gap-2">
                    <span className="text-mustard">→</span>
                    <span><strong>{a.label}</strong>{a.where ? <span className="text-base/70"> · {a.where}</span> : null}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
